import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import DistributorOrderPricing, { DistributorOrderHeader } from './DistributorOrderPricing';
import { validRetailPricing } from '../../features/orders/distributorRetailPricing';
import { distributorDraftAmount } from '../../features/orders/DistributorOrderDetails';
import {
  validDistributorOrderReceipt,
  distributorOrderNotice,
} from '../../features/orders/distributorOrderReceipt';
import DistributorOrderAddress from './DistributorOrderAddress';
import shippingOptions from '../../data/shippingOptions.json';
import { stateCode } from '../../data/usStates';
import { productLabel } from '../../utils/productLabel';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useLocale } from '../../i18n/LanguageContext';
import { formatDeliveryDate } from '../../utils/orderDates';
import useDistributorCashQuote from '../../hooks/useDistributorCashQuote';
import { OrderFormFooter } from './DistributorOrderCashQuote';
import { demoOrderingEnvironment } from '../../utils/demoOrderingEnvironment';
import '../../styles/distributor-order.css';

const emptyAddress = { street: '', city: '', state: '', zipCode: '' };

export default function DistributorOrderForm({
  clinicId,
  clinicMode = false,
  inDialog = false,
  heldOrder = null,
  settingsRevision,
  t,
  onSubmitted,
  onStateChange,
}) {
  const locale = useLocale();
  const demoOrderingDisabled = demoOrderingEnvironment();
  const session = useRef(captureSession()).current;
  const [options, setOptions] = useState(null);
  const [speed, setSpeed] = useState(
    heldOrder?.intent?.shippingSpeed || 'Priority Overnight - 10:30AM'
  );
  const [accepted, setAccepted] = useState(false);
  const [quote, setQuote] = useState(null);
  const [carrier, setCarrier] = useState(heldOrder?.intent?.preferredCarrier || 'fedex');
  const [lines, setLines] = useState(
    heldOrder?.intent?.lineItems?.map((line) => ({
      product: line.product?._id || line.product,
      quantity: String(line.quantity),
    })) || [{ product: '', quantity: '1' }]
  );
  const [po, setPo] = useState(heldOrder?.intent?.patientName || '');
  const [address, setAddress] = useState(heldOrder?.intent?.address || emptyAddress);
  const [hasDefaultAddress, setHasDefaultAddress] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [reload, setReload] = useState(0);
  const [requestKey, setRequestKey] = useState(() => window.crypto?.randomUUID?.() || '');
  const shippingRequest = useRef(0);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const pendingPayload = useRef(null);
  const pricing = options?.pricing;
  const cashEnabled = options?.cashBank?.enabled === true;
  const subtotal = distributorDraftAmount(lines, options?.products, pricing);
  const customerSubtotalCents = subtotal ? Number(subtotal.replace(/[$,.]/g, '')) : null;
  const dollarOrderingUnavailable = pricing?.pricingBasis === 'piece' && !cashEnabled;
  const enabled =
    options?.orderingEnabled &&
    (!clinicMode || options?.submissionEnabled === true) &&
    !dollarOrderingUnavailable;
  const pricingError = clinicMode
    ? 'Customer pricing is unavailable. Contact your distributor.'
    : 'Customer pricing is unavailable. Set a customer price before ordering.';
  const piecePricing = pricing?.pricingBasis === 'piece';
  const pricingAvailable =
    validRetailPricing(pricing) &&
    (!piecePricing ||
      lines.every((line) => {
        if (!line.product) return true;
        const product = options.products.find((row) => row._id === line.product);
        return pricing.categoryRates.some((rate) => rate.familyKey === product?.familyKey);
      }));
  const dirty = Boolean(
    po ||
      lines.some((line) => line.product || line.quantity !== '1') ||
      speed !== 'Priority Overnight - 10:30AM' ||
      (options &&
        (carrier !== (options.preferredCarrier || 'fedex') ||
          JSON.stringify(address) !==
            JSON.stringify(
              options.deliveryAddress
                ? { ...options.deliveryAddress, state: stateCode(options.deliveryAddress.state) }
                : emptyAddress
            )))
  );
  const draftPresent = useRef(false);
  draftPresent.current = options ? dirty : draftPresent.current || dirty;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    onStateChange?.({ busy: submitting, uncertain, dirty: draftPresent.current });
  }, [submitting, uncertain, dirty, onStateChange]);
  useEffect(() => {
    if (pendingPayload.current || !isSessionCurrent(session)) return;
    let active = true;
    const preserveDraft = draftPresent.current;
    setOptions(null);
    setError('');
    (heldOrder
      ? Promise.resolve({ data: { data: heldOrder } })
      : distributorService.orderOptions(clinicId)
    )
      .then((response) => {
        const data = response?.data?.data;
        if (!Array.isArray(data?.products)) throw new Error('Invalid order options');
        if (active && isSessionCurrent(session)) {
          setOptions(data);
          if (preserveDraft || heldOrder) return;
          setAddress(
            data.deliveryAddress
              ? { ...data.deliveryAddress, state: stateCode(data.deliveryAddress.state) }
              : { ...emptyAddress }
          );
          setHasDefaultAddress(Boolean(data.deliveryAddress));
          setCarrier(data.preferredCarrier || 'fedex');
          setLines((current) =>
            current.map((line) =>
              data.products.some((product) => product._id === line.product)
                ? line
                : { ...line, product: '' }
            )
          );
        }
      })
      .catch(() => {
        if (active && isSessionCurrent(session))
          setError('Order choices are unavailable. Please retry.');
      });
    return () => {
      active = false;
    };
  }, [clinicId, settingsRevision, reload, session, heldOrder]);
  useEffect(() => {
    let active = true;
    if (!isSessionCurrent(session)) return;
    const generation = ++shippingRequest.current;
    setQuote(null);
    if (heldOrder && reload === 0 && speed === heldOrder.intent?.shippingSpeed) {
      setQuote({ shippingSpeed: speed, deliveryDate: heldOrder.intent.deliveryDate });
      return;
    }
    distributorService
      .previewOrderShipping({ shippingSpeed: speed })
      .then((response) => {
        const data = response?.data?.data;
        if (!data?.deliveryDate || data.shippingSpeed !== speed)
          throw new Error('Invalid shipping quote');
        if (active && generation === shippingRequest.current && isSessionCurrent(session))
          setQuote(data);
      })
      .catch(() => {
        if (active && generation === shippingRequest.current && isSessionCurrent(session))
          setError('Delivery date is unavailable. Please retry.');
      });
    return () => {
      active = false;
    };
  }, [speed, reload, session, heldOrder]);
  useEffect(() => {
    const warn = (event) => {
      if (draftPresent.current || submitting || uncertain) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, submitting, uncertain]);
  const setLine = (index, field, value) => {
    setAccepted(false);
    setLines((current) =>
      current.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  };
  const setAddressField = (field, value) => {
    setAccepted(false);
    setAddress((current) => ({ ...current, [field]: value }));
  };
  const { cashQuote, invalidateCashQuote, quotingCash, reviewCashQuote, validateCashAcceptance } =
    useDistributorCashQuote({
      lines,
      address,
      po,
      carrier,
      speed,
      quote,
      pricing,
      options,
      session,
      clinicId,
      clinicMode,
      pricingAvailable,
      inFlight,
      pendingPayload,
      mounted,
      setError,
      setAccepted,
    });
  const submit = async (event) => {
    event.preventDefault();
    if (demoOrderingDisabled) return;
    if (inFlight.current || !mounted.current || !isSessionCurrent(session)) return;
    setError('');
    setNotice('');
    if (!pricingAvailable) {
      setError(pricingError);
      return;
    }
    if (cashEnabled && !validateCashAcceptance(accepted)) return;
    if (
      !enabled ||
      (clinicMode && !subtotal) ||
      (heldOrder && !accepted) ||
      !quote ||
      !requestKey ||
      lines.some(
        (line) =>
          !line.product || !Number.isSafeInteger(Number(line.quantity)) || Number(line.quantity) < 1
      )
    ) {
      setError('Choose products and whole quantities, then confirm the calculated delivery date.');
      return;
    }
    const selectedLines = lines.map((line) => ({
      product: line.product,
      size: options.products.find((p) => p._id === line.product)?.size,
      quantity: Number(line.quantity),
    }));
    if (selectedLines.some((line) => !line.size)) {
      setError('A product size could not be verified. Reload the order choices.');
      return;
    }
    const destination = Object.fromEntries(
      Object.entries(address).map(([key, value]) => [key, value.trim()])
    );
    if (Object.values(destination).some((value) => !value) || !stateCode(destination.state)) {
      setError('Enter a complete delivery address before ordering.');
      return;
    }
    const current = () => mounted.current && isSessionCurrent(session);
    inFlight.current = true;
    setSubmitting(true);
    pendingPayload.current ||= {
      ...(clinicMode ? { capability: 'clinic-orders-v1' } : {}),
      ...(heldOrder
        ? { expectedRevision: heldOrder.revision, accepted: true }
        : { idempotencyKey: requestKey }),
      ...(!heldOrder ? { clinic: clinicId, expectedPricingRevision: pricing.revision } : {}),
      lineItems: selectedLines,
      shippingScheduleVersion: 1,
      shippingSpeed: speed,
      deliveryDate: quote.deliveryDate,
      preferredCarrier: carrier,
      address: destination,
      ...(po.trim() ? { patientName: po.trim() } : {}),
      ...(cashEnabled ? { cashQuoteId: cashQuote.cashQuoteId } : {}),
    };
    try {
      const result = (
        await (heldOrder
          ? distributorService.editHeldOrder(heldOrder._id, pendingPayload.current)
          : distributorService.submitBankedOrder(pendingPayload.current))
      )?.data?.data;
      if (!current()) return;
      if (!validDistributorOrderReceipt(result)) throw new Error('Unconfirmed order');
      setUncertain(false);
      pendingPayload.current = null;
      setNotice(distributorOrderNotice(result));
      setRequestKey(window.crypto?.randomUUID?.() || '');
      setLines([{ product: '', quantity: '1' }]);
      setPo('');
      setReload((value) => value + 1);
      onSubmitted(result);
    } catch (failure) {
      if (!current()) return;
      const shortage = failure?.response?.data;
      if (
        failure?.response?.status === 409 &&
        ['CASH_QUOTE_CHANGED', 'CASH_QUOTE_REQUIRED'].includes(shortage?.code)
      ) {
        pendingPayload.current = null;
        setUncertain(false);
        invalidateCashQuote();
        setAccepted(false);
        setRequestKey(window.crypto?.randomUUID?.() || '');
        setError(
          'The cash quote changed or expired. Review the current delivery charge before submitting again.'
        );
      } else if (
        failure?.response?.status === 409 &&
        shortage?.code === 'CUSTOMER_PRICING_CHANGED'
      ) {
        // A definite precommit conflict may refresh choices, never replay the write automatically.
        pendingPayload.current = null;
        setUncertain(false);
        setOptions(null);
        setQuote(null);
        setNotice('Customer pricing changed. Review the updated rate before submitting again.');
        setReload((value) => value + 1);
      } else if (
        failure?.response?.status === 409 &&
        shortage?.code === 'SHIPPING_AVAILABILITY_CHANGED'
      ) {
        // This code is emitted only before creation, after checking for an existing request.
        pendingPayload.current = null;
        setUncertain(false);
        setAccepted(false);
        setQuote(null);
        setNotice(
          'Shipping availability changed. Review the updated delivery date before submitting again.'
        );
        setReload((value) => value + 1);
      } else if (
        failure?.response?.status === 409 &&
        shortage?.code === 'DOLLAR_ORDERING_UNAVAILABLE'
      ) {
        pendingPayload.current = null;
        setUncertain(false);
        setAccepted(false);
        setOptions(null);
        setQuote(null);
        setRequestKey(window.crypto?.randomUUID?.() || '');
        setError(
          'Dollar-bank ordering is unavailable. Contact MedEffects before placing this order.'
        );
        setReload((value) => value + 1);
      } else if (
        !clinicMode &&
        shortage?.code === 'INSUFFICIENT_BANK_UNITS' &&
        /^[a-z][a-z0-9_]{1,39}$/.test(shortage.familyKey) &&
        Number.isSafeInteger(shortage.availableUnits) &&
        Number.isSafeInteger(shortage.requiredUnits)
      ) {
        pendingPayload.current = null;
        setUncertain(false);
        setError({
          key: '{family} has {available} units; this order needs {required}.',
          values: {
            family: shortage.familyKey.replaceAll('_', ' '),
            available: shortage.availableUnits,
            required: shortage.requiredUnits,
          },
        });
      } else {
        setUncertain(true);
        setError(
          'Order could not be confirmed. Check your orders, then retry with the same request. No new request key was created.'
        );
      }
    } finally {
      inFlight.current = false;
      if (current()) setSubmitting(false);
    }
  };
  return (
    <section
      className="me-distributor-panel me-distributor-order rounded-xl border"
      aria-label={t('Distributor order')}
    >
      {!inDialog && (
        <DistributorOrderHeader {...{ heldOrder, clinicMode, cashEnabled, piecePricing, t }} />
      )}
      {error && (
        <p role="alert" className="me-do-feedback me-do-feedback-error">
          {t(error.key || error, error.values)}{' '}
          <button
            type="button"
            className="underline"
            disabled={submitting || uncertain}
            onClick={() => {
              setAccepted(false);
              setReload((n) => n + 1);
            }}
          >
            {t('Reload choices')}
          </button>
        </p>
      )}
      {notice && (
        <p role="status" className="me-do-feedback me-do-feedback-success">
          {typeof notice === 'string'
            ? t(notice)
            : t(notice.key, {
                ...notice.values,
                status: t(notice.values.status),
                deliveryStatus: t(notice.values.deliveryStatus),
              })}
        </p>
      )}
      {!options && !error && (
        <p role="status" className="me-do-feedback">
          {t('Loading order choices...')}
        </p>
      )}
      {options && (
        <>
          <DistributorOrderPricing
            {...{
              dollarOrderingUnavailable,
              enabled,
              pricingAvailable,
              piecePricing,
              cashEnabled,
              pricing,
              pricingError,
              locale,
              t,
            }}
          />
          {options.products.length === 0 && (
            <p className="me-do-feedback">
              {t(
                clinicMode
                  ? 'No products are available for this clinic.'
                  : 'No BLS bank products are available for this clinic.'
              )}
            </p>
          )}
          <form onSubmit={submit} className="me-do-form">
            <fieldset
              disabled={submitting || uncertain || !enabled || options.products.length === 0}
              className="me-do-form-fields disabled:opacity-60"
            >
              <div className="me-do-section">
                <h3 className="me-do-section-title">{t('Delivery address')}</h3>
                <DistributorOrderAddress {...{ address, setAddressField, t }} />
                <p className="me-do-hint">
                  {t(
                    hasDefaultAddress
                      ? 'Prefilled from this clinic. Changes apply to this order only.'
                      : 'Enter a delivery address for this order. Changes apply to this order only.'
                  )}
                </p>
              </div>
              <div className="me-do-section">
                <h3 className="me-do-section-title">{t('Products')}</h3>
                {lines.map((line, index) => (
                  <div key={index} className="me-do-product-row">
                    <label className="me-do-product-select">
                      {t('Product')} {index + 1}
                      <select
                        className="me-do-input block w-full"
                        value={line.product}
                        onChange={(event) => setLine(index, 'product', event.target.value)}
                        required
                      >
                        <option value="">{t('Choose product')}</option>
                        {options.products.map((product) => (
                          <option key={product._id} value={product._id}>
                            {productLabel(product.name, product.size)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="me-do-quantity">
                      {t('Quantity')}
                      <input
                        className="me-do-input block w-full"
                        type="number"
                        min="1"
                        step="1"
                        value={line.quantity}
                        onChange={(event) => setLine(index, 'quantity', event.target.value)}
                        required
                      />
                    </label>
                    {lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setAccepted(false);
                          setLines((current) => current.filter((_, i) => i !== index));
                        }}
                        className="me-do-remove"
                      >
                        {t('Remove')}
                      </button>
                    )}
                  </div>
                ))}
                {lines.length < 20 && (
                  <button
                    type="button"
                    className="me-do-add-product"
                    onClick={() => {
                      setAccepted(false);
                      setLines((current) => [...current, { product: '', quantity: '1' }]);
                    }}
                  >
                    {t('Add product')}
                  </button>
                )}
              </div>
              <div className="me-do-section">
                <h3 className="me-do-section-title">{t('Shipping')}</h3>
                <div className="me-do-shipping-grid">
                  <label>
                    {t('Shipping option')}
                    <select
                      className="me-do-input block w-full"
                      value={speed}
                      onChange={(event) => {
                        setAccepted(false);
                        setSpeed(event.target.value);
                        setError('');
                      }}
                    >
                      {shippingOptions.map((option) => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="me-do-delivery-date" role="status">
                    <span>{t('Calculated delivery date')}</span>
                    <strong>
                      {formatDeliveryDate(quote?.deliveryDate, locale) || t('Checking...')}
                    </strong>
                  </div>
                  <label>
                    {t('Preferred carrier')}
                    <select
                      className="me-do-input block w-full"
                      value={carrier}
                      onChange={(event) => {
                        setAccepted(false);
                        setCarrier(event.target.value);
                      }}
                    >
                      <option value="fedex">FedEx</option>
                      <option value="ups">UPS</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="me-do-section me-do-section-final">
                <label className="block">
                  {t('Customer PO (optional)')}
                  <input
                    className="me-do-input block w-full"
                    maxLength={120}
                    value={po}
                    onChange={(event) => {
                      setAccepted(false);
                      setPo(event.target.value);
                    }}
                  />
                </label>
              </div>
            </fieldset>
            <OrderFormFooter
              t={t}
              state={{
                demoOrderingDisabled,
                clinicMode,
                subtotal,
                cashEnabled,
                heldOrder,
                cashQuote,
                customerSubtotalCents,
                piecePricing,
                reviewCashQuote,
                submitting,
                uncertain,
                enabled,
                quotingCash,
                accepted,
                setAccepted,
                pricingAvailable,
                productsPresent: options.products.length > 0,
                quotePresent: Boolean(quote),
                requestKey,
              }}
            />
          </form>
        </>
      )}
    </section>
  );
}
